import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CategoryIcon,
  IconCheck,
  IconClose,
  IconDownload,
  IconPaperclip,
  IconTrash,
} from '../components/icons';
import { DetailHeader } from '../components/layout';
import { StatusBadge, categoryName } from '../components/receipts';
import {
  AmountInput,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Field,
  Input,
  List,
  Row,
  Segmented,
  Select,
  Sheet,
  Skeleton,
  Spinner,
  Textarea,
  useToast,
} from '../components/ui';
import { useI18n } from '../i18n';
import {
  api,
  ApiError,
  documentUrl,
  type DocumentKind,
  type ExpenseDetail,
  type ExpenseDocument,
  type PaymentMethod,
} from '../lib/api';
import {
  useCategories,
  useDeleteExpense,
  useExpense,
  usePeople,
  useReceiptInvalidation,
  useReimburse,
  useUnreimburse,
  useUpdateExpense,
} from '../lib/queries';
import { transitions } from '../lib/motion';
import { accentVar, centsToInput, cn, parseAmountInput } from '../lib/ui';
import { PAYMENT_HELP } from './AddReceipt';
import { useErrorMessage } from './Auth';

/**
 * Full-screen document viewer.
 *
 * Tapping a receipt should show it, not hand you a download. Previously only
 * the 48px thumbnail was a link and it dropped you into a browser tab, which
 * loses the app and, on a phone, is a one-way trip.
 *
 * Images get a fit/fill toggle rather than pinch-zoom: page zoom is disabled
 * app-wide, so a receipt photographed at an angle needs some way to be read
 * closely. PDFs are handed to the browser's own viewer, which is better than
 * anything worth rebuilding here.
 */
function DocumentViewer({
  document: doc,
  onClose,
}: {
  document: ExpenseDocument | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [filled, setFilled] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!doc) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.document.addEventListener('keydown', onKey);
    const previous = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.removeEventListener('keydown', onKey);
      window.document.body.style.overflow = previous;
    };
  }, [doc, onClose]);

  useEffect(() => {
    setFilled(false);
    setFailed(false);
  }, [doc?.id]);

  const isPdf = doc?.mimeType === 'application/pdf';
  const isImage = doc?.mimeType?.startsWith('image/') ?? false;

  return (
    <AnimatePresence>
      {doc && doc.paperlessDocumentId ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transitions.fade}
          className="fixed inset-0 z-[70] flex flex-col bg-black/92"
          role="dialog"
          aria-modal="true"
          aria-label={doc.filename}
        >
          <div className="safe-top flex shrink-0 items-center gap-2 px-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="press rounded-full p-2.5 text-white/90 hover:bg-white/10"
            >
              <IconClose size={20} />
            </button>
            <div className="min-w-0 flex-1 text-center">
              <div className="truncate text-[14px] font-medium text-white">{doc.filename}</div>
              <div className="text-[12px] text-white/60">
                {t(`documentKind.${doc.kind}` as 'documentKind.receipt')}
              </div>
            </div>
            <a
              href={documentUrl(doc.paperlessDocumentId, 'download')}
              aria-label={t('detail.download')}
              className="press rounded-full p-2.5 text-white/90 hover:bg-white/10"
            >
              <IconDownload size={19} />
            </a>
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-3">
            {isImage && !failed ? (
              <button
                type="button"
                onClick={() => setFilled((value) => !value)}
                aria-label={t('detail.viewerZoom')}
                className="block h-full w-full cursor-zoom-in"
              >
                <img
                  src={documentUrl(doc.paperlessDocumentId, 'preview')}
                  alt={doc.filename}
                  onError={() => setFailed(true)}
                  className={cn(
                    'mx-auto rounded-[10px]',
                    filled ? 'w-auto max-w-none' : 'h-full w-full object-contain',
                  )}
                />
              </button>
            ) : isPdf && !failed ? (
              <iframe
                src={documentUrl(doc.paperlessDocumentId, 'preview')}
                title={doc.filename}
                className="h-full w-full rounded-[10px] border-0 bg-white"
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                <IconPaperclip size={28} className="text-white/50" />
                <p className="text-[14px] text-white/70">
                  {failed ? t('detail.viewerFailed') : t('detail.viewerUnsupported')}
                </p>
                <a
                  href={documentUrl(doc.paperlessDocumentId, 'download')}
                  className="press rounded-full bg-white/15 px-4 py-2 text-[14px] text-white"
                >
                  {t('detail.download')}
                </a>
              </div>
            )}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

const PAYMENT_METHODS: PaymentMethod[] = ['out_of_pocket', 'hsa_card', 'other'];

function DocumentCard({
  document,
  onDelete,
  onOpen,
}: {
  document: ExpenseDocument;
  onDelete: () => void;
  onOpen: () => void;
}) {
  const { t } = useI18n();

  if (document.status === 'pending') {
    return (
      <div className="flex items-center gap-3 px-4 py-3.5">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-[var(--surface-sunken)] text-[var(--ink-muted)]">
          <Spinner size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] text-[var(--ink)]">
            {t('detail.documentProcessing')}
          </div>
          <p className="mt-0.5 text-[12px] text-[var(--ink-secondary)]">
            {t('detail.documentProcessingHelp')}
          </p>
        </div>
      </div>
    );
  }

  if (document.status === 'failed' || !document.paperlessDocumentId) {
    return (
      <div className="flex items-center gap-3 px-4 py-3.5">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-[var(--critical-soft)] text-[var(--critical)]">
          <IconPaperclip size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] text-[var(--ink)]">{t('detail.documentFailed')}</div>
          <p className="mt-0.5 text-[12px] text-[var(--ink-secondary)]">
            {t('detail.documentFailedHelp')}
          </p>
        </div>
        <button
          type="button"
          onClick={onDelete}
          aria-label={t('common.remove')}
          className="press shrink-0 rounded-full p-2 text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]"
        >
          <IconTrash size={17} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      {/* The whole row opens it: a 48px thumbnail is a poor tap target, and
          the filename is the part people actually aim at. */}
      <button
        type="button"
        onClick={onOpen}
        className="press flex min-w-0 flex-1 items-center gap-3 text-left"
        aria-label={t('a11y.documentPreview', { filename: document.filename })}
      >
        <img
          src={documentUrl(document.paperlessDocumentId, 'thumb')}
          alt=""
          loading="lazy"
          className="h-12 w-12 shrink-0 rounded-[10px] border border-[var(--line)] object-cover"
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] text-[var(--ink)]">{document.filename}</span>
          <span className="mt-0.5 block text-[12px] text-[var(--ink-secondary)]">
            {t(`documentKind.${document.kind}` as 'documentKind.receipt')}
          </span>
        </span>
      </button>
      <a
        href={documentUrl(document.paperlessDocumentId, 'download')}
        className="press shrink-0 rounded-full p-2 text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]"
        aria-label={t('detail.download')}
      >
        <IconDownload size={17} />
      </a>
      <button
        type="button"
        onClick={onDelete}
        aria-label={t('common.remove')}
        className="press shrink-0 rounded-full p-2 text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]"
      >
        <IconTrash size={17} />
      </button>
    </div>
  );
}

function ReimburseSheet({
  open,
  onClose,
  expense,
}: {
  open: boolean;
  onClose: () => void;
  expense: ExpenseDetail;
}) {
  const { t, money, locale, currency } = useI18n();
  const toast = useToast();
  const reimburse = useReimburse(expense.id);

  const outstanding = Math.max(expense.amountCents - expense.reimbursedCents, 0);
  const [amount, setAmount] = useState(centsToInput(outstanding));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');

  const currencySymbol =
    new Intl.NumberFormat(locale, { style: 'currency', currency })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? currency;

  const submit = () => {
    const cents = parseAmountInput(amount);
    reimburse.mutate(
      { amountCents: cents ?? outstanding, date, note: note.trim() || undefined },
      {
        onSuccess: () => {
          toast.show(t('common.saved'), 'positive');
          onClose();
        },
      },
    );
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('detail.reimburseTitle')}
      footer={
        <Button fullWidth size="lg" loading={reimburse.isPending} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <p className="mb-4 text-[14px] leading-relaxed text-[var(--ink-secondary)]">
        {t('detail.reimburseBody')}
      </p>

      <div className="space-y-4">
        <div>
          <div className="mb-2 text-[13px] font-medium text-[var(--ink-secondary)]">
            {t('detail.reimburseAmount')}
          </div>
          <AmountInput value={amount} onChange={setAmount} currencySymbol={currencySymbol} />
          <button
            type="button"
            onClick={() => setAmount(centsToInput(outstanding))}
            className="mt-2 text-[13px] text-[var(--accent)]"
          >
            {t('detail.reimburseFull', { amount: money(outstanding) })}
          </button>
        </div>

        <Field label={t('detail.reimburseDate')}>
          <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>

        <Field label={t('detail.reimburseNote')} optional>
          <Input
            value={note}
            placeholder={t('detail.reimburseNotePlaceholder')}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      </div>
    </Sheet>
  );
}

function EditSheet({
  open,
  onClose,
  expense,
}: {
  open: boolean;
  onClose: () => void;
  expense: ExpenseDetail;
}) {
  const { t, locale, currency } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();
  const update = useUpdateExpense(expense.id);
  const people = usePeople(expense.ledgerId);
  const categories = useCategories();

  const [form, setForm] = useState({
    amount: centsToInput(expense.amountCents),
    personId: expense.personId,
    categoryId: expense.categoryId,
    serviceDate: expense.serviceDate,
    provider: expense.provider ?? '',
    description: expense.description ?? '',
    notes: expense.notes ?? '',
    paymentMethod: expense.paymentMethod,
    eligibility: expense.eligibility,
  });

  const currencySymbol =
    new Intl.NumberFormat(locale, { style: 'currency', currency })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? currency;

  const submit = () => {
    update.mutate(
      {
        amountCents: parseAmountInput(form.amount) ?? expense.amountCents,
        personId: form.personId,
        categoryId: form.categoryId,
        serviceDate: form.serviceDate,
        provider: form.provider.trim() || null,
        description: form.description.trim() || null,
        notes: form.notes.trim() || null,
        paymentMethod: form.paymentMethod,
        eligibility: form.eligibility,
      },
      {
        onSuccess: () => {
          toast.show(t('common.saved'), 'positive');
          onClose();
        },
        onError: (cause: unknown) => toast.show(toMessage(cause), 'critical'),
      },
    );
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('form.editReceipt')}
      size="lg"
      footer={
        <Button fullWidth size="lg" loading={update.isPending} onClick={submit}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="space-y-4 pt-1">
        <div>
          <div className="mb-2 text-[13px] font-medium text-[var(--ink-secondary)]">
            {t('form.amount')}
          </div>
          <AmountInput
            value={form.amount}
            onChange={(amount) => setForm({ ...form, amount })}
            currencySymbol={currencySymbol}
          />
        </div>

        <Field label={t('form.person')} optional>
          <div className="flex flex-wrap gap-2">
            {people.data?.people.map((person) => (
              <button
                key={person.id}
                type="button"
                onClick={() =>
                  setForm({ ...form, personId: form.personId === person.id ? null : person.id })
                }
                className="press inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[14px]"
                style={
                  form.personId === person.id
                    ? {
                        background: accentVar(person.color),
                        borderColor: 'transparent',
                        color: 'white',
                      }
                    : undefined
                }
              >
                {person.name}
              </button>
            ))}
          </div>
        </Field>

        <Field label={t('form.category')} optional>
          <div className="flex flex-wrap gap-2">
            {categories.data?.categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    categoryId: form.categoryId === category.id ? null : category.id,
                  })
                }
                className="press inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[14px]"
                style={
                  form.categoryId === category.id
                    ? {
                        background: accentVar(category.color),
                        borderColor: 'transparent',
                        color: 'white',
                      }
                    : undefined
                }
              >
                <CategoryIcon glyph={category.icon} size={15} />
                {categoryName(category, locale)}
              </button>
            ))}
          </div>
        </Field>

        <Field label={t('form.serviceDate')}>
          <Input
            type="date"
            value={form.serviceDate}
            onChange={(event) => setForm({ ...form, serviceDate: event.target.value })}
          />
        </Field>

        <Field label={t('form.provider')} optional>
          <Input
            value={form.provider}
            onChange={(event) => setForm({ ...form, provider: event.target.value })}
          />
        </Field>

        <Field label={t('payment.label')} hint={t(PAYMENT_HELP[form.paymentMethod])}>
          <Segmented
            className="w-full"
            value={form.paymentMethod}
            onChange={(paymentMethod) => setForm({ ...form, paymentMethod })}
            options={PAYMENT_METHODS.map((method) => ({
              value: method,
              label: t(`payment.${method}` as 'payment.out_of_pocket'),
            }))}
          />
        </Field>

        <Field
          label={t('eligibility.label')}
          hint={form.eligibility === 'unsure' ? t('eligibility.unsureHelp') : undefined}
        >
          <Segmented
            className="w-full"
            value={form.eligibility}
            onChange={(eligibility) => setForm({ ...form, eligibility })}
            options={(['eligible', 'unsure', 'ineligible'] as const).map((value) => ({
              value,
              label: t(`eligibility.${value}` as 'eligibility.eligible'),
            }))}
          />
        </Field>

        <Field label={t('form.description')} optional>
          <Input
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
        </Field>

        <Field label={t('form.notes')} optional>
          <Textarea
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
          />
        </Field>
      </div>
    </Sheet>
  );
}

/** Offered in the order an HSA filer actually reaches for them. */
const DOCUMENT_KINDS: DocumentKind[] = [
  'receipt',
  'invoice',
  'eob',
  'prescription',
  'statement',
  'tax_form',
  'other',
];

export function ReceiptDetailScreen({ paperlessReady }: { paperlessReady: boolean }) {
  const { t, money, date, dateTime, locale } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useReceiptInvalidation();

  const query = useExpense(id);
  const unreimburse = useUnreimburse(id ?? '');
  const remove = useDeleteExpense();

  const [showReimburse, setShowReimburse] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [viewing, setViewing] = useState<ExpenseDocument | null>(null);
  const [purgeDocuments, setPurgeDocuments] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // What the next file is. Cairn has always stored a kind per document — an
  // explanation of benefits and a 1099-SA are not receipts, and Paperless files
  // them under different document types — but nothing ever let anyone pick one,
  // so every upload was recorded as a receipt.
  const [documentKind, setDocumentKind] = useState<DocumentKind>('receipt');

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) await api.uploadDocument(id!, file, documentKind);
    },
    onSuccess: () => {
      invalidate();
      toast.show(t('detail.documentProcessing'));
    },
  });

  const removeDocument = useMutation({
    mutationFn: (documentId: string) => api.deleteDocument(id!, documentId),
    onSuccess: invalidate,
  });

  if (query.isLoading) {
    return (
      <>
        <DetailHeader title={t('detail.title')} onBack={() => navigate(-1)} />
        <div className="space-y-4 px-4 py-4 lg:px-8">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </>
    );
  }

  const expense = query.data?.expense;

  /**
   * "Could not load it" is not "it does not exist".
   *
   * This used to render the not-found screen for any absent data, so a dropped
   * connection, a restarting container, or iOS suspending the app mid-request
   * told someone their receipt was gone — moments after they had saved it. The
   * two cases are now told apart by what the server actually said, and only a
   * genuine 404 claims the receipt is missing. Anything else offers a retry,
   * because it is almost always temporary.
   */
  if (!expense) {
    const cause = query.error;
    const missing = cause instanceof ApiError && cause.status === 404;

    return (
      <>
        <DetailHeader
          // A short label, not a sentence: this is a title bar, and
          // "We could not find that." reads as an error dialog wedged into one.
          title={missing ? t('errors.notFoundTitle') : t('errors.couldNotLoad')}
          onBack={() => navigate('/receipts')}
        />
        <div className="px-4 py-10 text-center">
          <p className="mx-auto max-w-[36ch] text-[14px] leading-snug text-[var(--ink-secondary)]">
            {missing ? t('errors.notFoundBody') : t('errors.couldNotLoadBody')}
          </p>
          {missing ? null : (
            <div className="mt-4 flex justify-center">
              <Button
                variant="secondary"
                loading={query.isFetching}
                onClick={() => void query.refetch()}
              >
                {t('common.retry')}
              </Button>
            </div>
          )}
        </div>
      </>
    );
  }

  const outstanding = Math.max(expense.amountCents - expense.reimbursedCents, 0);
  const statusHelp =
    expense.status === 'reimbursed'
      ? t('status.reimbursedHelp', { date: date(expense.reimbursedDate ?? expense.updatedAt) })
      : expense.status === 'partial'
        ? t('status.partialHelp', {
            claimed: money(expense.reimbursedCents),
            total: money(expense.amountCents),
          })
        : expense.status === 'ineligible'
          ? t('status.ineligibleHelp')
          : t('status.openHelp');

  return (
    <>
      <DetailHeader
        title={t('detail.title')}
        onBack={() => navigate(-1)}
        action={
          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="press rounded-full px-2 py-1.5 text-[15px] text-[var(--accent)]"
          >
            {t('common.edit')}
          </button>
        }
      />

      <div className="space-y-4 px-4 py-4 lg:px-8">
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-[19px] font-semibold tracking-tight text-[var(--ink)]">
                {expense.provider || expense.description || t('common.uncategorized')}
              </h2>
              <p className="mt-1 text-[14px] text-[var(--ink-secondary)]">
                {date(expense.serviceDate, 'full')}
              </p>
            </div>
            <StatusBadge status={expense.status} />
          </div>

          <div className="numeric mt-5 text-[40px] font-semibold leading-none text-[var(--ink)]">
            {money(expense.amountCents)}
          </div>
          <p className="mt-2 text-[13px] leading-snug text-[var(--ink-secondary)]">{statusHelp}</p>

          <div className="mt-5 flex gap-2.5">
            {expense.status === 'reimbursed' ? (
              <Button variant="secondary" fullWidth onClick={() => unreimburse.mutate()}>
                {t('detail.undoReimbursed')}
              </Button>
            ) : expense.paymentMethod === 'out_of_pocket' &&
              expense.eligibility !== 'ineligible' ? (
              <Button
                fullWidth
                icon={<IconCheck size={18} />}
                onClick={() => setShowReimburse(true)}
              >
                {outstanding === expense.amountCents
                  ? t('detail.markReimbursed')
                  : t('detail.markReimbursedPartial')}
              </Button>
            ) : null}
          </div>
        </Card>

        <section>
          <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2 px-1">
            <h3 className="text-[15px] font-semibold text-[var(--ink)]">{t('detail.documents')}</h3>
            {paperlessReady ? (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  className="sr-only"
                  onChange={(event) => {
                    upload.mutate(Array.from(event.target.files ?? []));
                    event.target.value = '';
                  }}
                />
                <div className="flex min-w-0 items-center gap-2">
                  {/* What is being added, beside the button that adds it. A
                      1099-SA and an explanation of benefits are not receipts,
                      and Paperless files each under its own document type. */}
                  <Select
                    aria-label={t('documentKind.label')}
                    value={documentKind}
                    onChange={(event) => setDocumentKind(event.target.value as DocumentKind)}
                    className="w-auto max-w-[180px] py-1.5 text-[13px]"
                  >
                    {DOCUMENT_KINDS.map((value) => (
                      <option key={value} value={value}>
                        {t(`documentKind.${value}` as 'documentKind.receipt')}
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={upload.isPending}
                    onClick={() => fileInput.current?.click()}
                  >
                    {t('detail.addDocument')}
                  </Button>
                </div>
              </>
            ) : null}
          </div>

          {expense.documents.length === 0 ? (
            <Card>
              <div className="text-[14px] text-[var(--ink)]">{t('detail.noDocuments')}</div>
              <p className="mt-1 text-[13px] leading-snug text-[var(--ink-secondary)]">
                {paperlessReady ? t('detail.noDocumentsHelp') : t('errors.paperlessNotConfigured')}
              </p>
            </Card>
          ) : (
            <div className="card divide-y divide-[var(--line)] overflow-hidden">
              {expense.documents.map((document) => (
                <DocumentCard
                  key={document.id}
                  document={document}
                  onDelete={() => removeDocument.mutate(document.id)}
                  onOpen={() => setViewing(document)}
                />
              ))}
            </div>
          )}
        </section>

        <List>
          {expense.personName ? (
            <Row
              label={t('form.person')}
              value={
                <span className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: accentVar(expense.personColor) }}
                    aria-hidden="true"
                  />
                  {expense.personName}
                </span>
              }
            />
          ) : null}
          {expense.categoryNameEn ? (
            <Row
              label={t('form.category')}
              value={categoryName(
                { nameEn: expense.categoryNameEn, nameZh: expense.categoryNameZh ?? '' },
                locale,
              )}
            />
          ) : null}
          <Row
            label={t('payment.label')}
            value={t(`payment.${expense.paymentMethod}` as 'payment.out_of_pocket')}
          />
          {expense.eligibility !== 'eligible' ? (
            <Row
              label={t('eligibility.label')}
              value={t(`eligibility.${expense.eligibility}` as 'eligibility.eligible')}
            />
          ) : null}
          {expense.reimbursedCents > 0 ? (
            <Row label={t('insights.claimed')} value={money(expense.reimbursedCents)} />
          ) : null}
          {expense.description ? (
            <Row label={t('form.description')} value={expense.description} />
          ) : null}
          {expense.notes ? <Row label={t('form.notes')} hint={expense.notes} /> : null}
          <Row label={t('detail.addedOn', { date: dateTime(expense.createdAt) })} />
        </List>

        <Button
          variant="ghost"
          fullWidth
          className="text-[var(--critical)]"
          icon={<IconTrash size={17} />}
          onClick={() => setShowDelete(true)}
        >
          {t('form.deleteReceipt')}
        </Button>
      </div>

      <ReimburseSheet
        open={showReimburse}
        onClose={() => setShowReimburse(false)}
        expense={expense}
      />
      <EditSheet open={showEdit} onClose={() => setShowEdit(false)} expense={expense} />

      <DocumentViewer document={viewing} onClose={() => setViewing(null)} />

      <ConfirmDialog
        open={showDelete}
        title={t('form.deleteConfirmTitle')}
        body={t('form.deleteConfirmBody')}
        confirmLabel={t('common.delete')}
        destructive
        extra={
          expense.documents.length > 0 ? (
            <Checkbox
              checked={purgeDocuments}
              onChange={setPurgeDocuments}
              label={t('form.deleteAlsoDocument')}
            />
          ) : null
        }
        onCancel={() => setShowDelete(false)}
        onConfirm={() =>
          remove.mutate(
            { id: expense.id, purgeDocuments },
            { onSuccess: () => navigate('/receipts', { replace: true }) },
          )
        }
      />
    </>
  );
}
